import { observable, computed, action, makeObservable } from 'mobx';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import AmountConvert from 'common/amountConvert';
import { conversionPayFactor } from 'common/constants';

export class Range implements IRange {
    private _minRange: number;

    private _midRange: number;

    private _maxRange: number;

    private _rangeIsoCurrencyCode: string;

    @observable private _currentIsoCurrencyCode: string;

    private _isHourly: boolean;

    private readonly _currencyConversion: CurrencyConversion;

    constructor(
        rangeDto: IRange,
        isoCurrencyCode: string,
        isHourly: boolean,
        currencyConversion: CurrencyConversion) {
        makeObservable(this);
        this._minRange = rangeDto.minRange;
        this._midRange = rangeDto.midRange;
        this._maxRange = rangeDto.maxRange;
        this._rangeIsoCurrencyCode = isoCurrencyCode;
        this._currentIsoCurrencyCode = isoCurrencyCode;
        this._isHourly = isHourly;
        this._currencyConversion = Coalesce.singleValue(currencyConversion, () => new CurrencyConversion(null));
    }

    get minRange(): number {
        return this._minRange;
    }

    get midRange(): number {
        return this._midRange;
    }

    get maxRange(): number {
        return this._maxRange;
    }

    get convertedMinRange(): number {
        return this._currencyConversion.convertCurrency(
            this._rangeIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._minRange);
    }

    get convertedMaxRange(): number {
        return this._currencyConversion.convertCurrency(
            this._rangeIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._maxRange);
    }

    get rangeIsoCurrencyCode(): string {
        return this._rangeIsoCurrencyCode;
    }

    @computed
    get currentIsoCurrencyCode(): string {
        return this._currentIsoCurrencyCode;
    }

    get isHourly(): boolean {
        return this._isHourly;
    }

    @action setRangeIsoCurrencyCode(code: string): void {
        this._rangeIsoCurrencyCode = code;
    }

    @action
    convertToHourly(currentPay: number, annualStandardWorkHours: number): void {
        this._isHourly = true;
        const minimumRangeAsAnnual = conversionPayFactor * currentPay;
        if (this.convertedMaxRange < minimumRangeAsAnnual) {
            return;
        }

        const hourlyMinRange = AmountConvert.convertToHourly(this.minRange, annualStandardWorkHours);
        const hourlyMaxRange = AmountConvert.convertToHourly(this.maxRange, annualStandardWorkHours);
        this._minRange = hourlyMinRange;
        this._maxRange = hourlyMaxRange;
    }

    @action
    convertToAnnual(currentPay: number, annualStandardWorkHours: number): void {
        const maximumRangeAsHourly = currentPay / conversionPayFactor;
        if (this.convertedMinRange > maximumRangeAsHourly) {
            return;
        }

        const annualMinRange =
            AmountConvert.convertToAnnual(this.minRange, annualStandardWorkHours, true);
        const annualMaxRange =
            AmountConvert.convertToAnnual(this.maxRange, annualStandardWorkHours, true);
        this._minRange = annualMinRange;
        this._maxRange = annualMaxRange;
    }
}